import { useState, useEffect, useCallback } from 'react';
import { aiModelsAPI, languagesAPI, adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Trash2, Star, Save, Loader2, Building } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPageShell, EmptyRow } from '../components/AdminShared';

const PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google' },
  { value: 'azure', label: 'Azure Speech Translation' },
];

export default function AdminAIModelsPage() {
  const [models, setModels] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [langMap, setLangMap] = useState({});
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgLangMap, setOrgLangMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editModel, setEditModel] = useState(null);
  const [form, setForm] = useState({ name: '', provider: 'openai', model: '' });

  const load = useCallback(async () => {
    try {
      const [mRes, lRes, mapRes, orgRes] = await Promise.all([
        aiModelsAPI.list(), languagesAPI.getAll(), aiModelsAPI.getLanguageMap(),
        adminAPI.listOrgs().catch(() => ({ data: [] })),
      ]);
      setModels(mRes.data);
      const allLangs = [...(lRes.data.sla || []), ...(lRes.data.supported || [])];
      setLanguages(allLangs.sort((a, b) => a.name.localeCompare(b.name)));
      const map = {};
      (mapRes.data || []).forEach(a => { map[a.language_code] = a.model_id; });
      setLangMap(map);
      setOrgs(orgRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load org-level overrides when org is selected
  const loadOrgMap = useCallback(async (orgId) => {
    if (!orgId) { setOrgLangMap({}); return; }
    try {
      const res = await aiModelsAPI.getOrgLanguageMap(orgId);
      const map = {};
      (res.data || []).forEach(a => { map[a.language_code] = a.model_id; });
      setOrgLangMap(map);
    } catch (e) { console.error(e); setOrgLangMap({}); }
  }, []);

  useEffect(() => { loadOrgMap(selectedOrgId); }, [selectedOrgId, loadOrgMap]);

  const handleAddModel = async () => {
    if (!form.name || !form.provider || !form.model) { toast.error('All fields required'); return; }
    try {
      if (editModel) {
        await aiModelsAPI.update(editModel.id, form);
        toast.success('Model updated');
      } else {
        await aiModelsAPI.create(form);
        toast.success('Model added');
      }
      setShowAdd(false);
      setEditModel(null);
      setForm({ name: '', provider: 'openai', model: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this model? Language assignments using it will be removed.')) return;
    try { await aiModelsAPI.remove(id); toast.success('Model deleted'); load(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const handleSetDefault = async (id) => {
    try { await aiModelsAPI.setDefault(id); toast.success('Default model updated'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const handleSaveSystemAssignments = async () => {
    setSaving(true);
    try {
      const assignments = languages.map(l => ({
        language_code: l.code,
        model_id: langMap[l.code] || '',
      }));
      await aiModelsAPI.bulkSetLanguageModels({ assignments });
      toast.success('System-level assignments saved');
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleSaveOrgAssignments = async () => {
    if (!selectedOrgId) { toast.error('Select an organization first'); return; }
    setSavingOrg(true);
    try {
      const assignments = languages.map(l => ({
        language_code: l.code,
        model_id: orgLangMap[l.code] || '',
      }));
      await aiModelsAPI.bulkSetOrgLanguageModels({ org_id: selectedOrgId, assignments });
      toast.success('Organization overrides saved');
    } catch (e) { toast.error('Failed to save org overrides'); }
    setSavingOrg(false);
  };

  const openEdit = (m) => {
    setEditModel(m);
    setForm({ name: m.name, provider: m.provider, model: m.model });
    setShowAdd(true);
  };

  const defaultModel = models.find(m => m.is_default);

  // Resolve the effective model for a language, considering org override
  const getEffectiveModel = (langCode) => {
    if (selectedOrgId && orgLangMap[langCode]) {
      return { modelId: orgLangMap[langCode], source: 'org' };
    }
    if (langMap[langCode]) {
      return { modelId: langMap[langCode], source: 'system' };
    }
    return { modelId: null, source: 'default' };
  };

  return (
    <AdminPageShell
      loading={loading}
      title="AI Language Models"
      subtitle="Configure AI models and assign them to languages for interpretation"
      testId="admin-ai-models-page"
    >
      <Tabs defaultValue="assignments">
        <TabsList className="grid w-full grid-cols-2 h-10 mb-6">
          <TabsTrigger value="assignments" data-testid="tab-assignments">Language Assignments</TabsTrigger>
          <TabsTrigger value="models" data-testid="tab-models">Manage Models</TabsTrigger>
        </TabsList>

        {/* ── LANGUAGE ASSIGNMENTS ── */}
        <TabsContent value="assignments">
          {/* Org Selector */}
          <div className="flex items-center gap-4 mb-4 p-3 rounded-lg border bg-card/50">
            <Building className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div className="flex items-center gap-2 flex-1">
              <Label className="text-sm whitespace-nowrap">Organization Override:</Label>
              <Select value={selectedOrgId || '_system'} onValueChange={v => setSelectedOrgId(v === '_system' ? '' : v)}>
                <SelectTrigger className="w-64 h-9" data-testid="org-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_system">System Defaults (all orgs)</SelectItem>
                  {orgs.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!selectedOrgId ? (
              <Button onClick={handleSaveSystemAssignments} disabled={saving} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="save-assignments-button">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save System Defaults
              </Button>
            ) : (
              <Button onClick={handleSaveOrgAssignments} disabled={savingOrg} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="save-org-assignments-button">
                {savingOrg ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Org Overrides
              </Button>
            )}
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            {selectedOrgId
              ? <>Override model per language for <strong>{orgs.find(o => o.id === selectedOrgId)?.name}</strong>. Blank = inherit system default.</>
              : <>System defaults for all organizations. Default model: <Badge variant="outline" className="ml-1 text-xs font-mono">{defaultModel?.name || 'OpenAI GPT-5.2'}</Badge></>
            }
          </p>

          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-[180px]">Language</TableHead>
                <TableHead>{selectedOrgId ? 'Org Override' : 'System Model'}</TableHead>
                {selectedOrgId && <TableHead className="w-[200px]">System Default</TableHead>}
                <TableHead className="w-[120px]">Source</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {languages.map(lang => {
                  const effective = getEffectiveModel(lang.code);
                  const systemModel = models.find(m => m.id === langMap[lang.code]);
                  const currentMap = selectedOrgId ? orgLangMap : langMap;
                  const setMap = selectedOrgId ? setOrgLangMap : setLangMap;
                  return (
                    <TableRow key={lang.code} data-testid={`lang-row-${lang.code}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{lang.name}</span>
                          <Badge variant="outline" className="text-[10px] font-mono">{lang.code}</Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={currentMap[lang.code] || '_default'}
                          onValueChange={v => setMap(prev => ({ ...prev, [lang.code]: v === '_default' ? '' : v }))}
                        >
                          <SelectTrigger className="w-64 h-9" data-testid={`model-select-${lang.code}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_default">
                              {selectedOrgId ? 'Inherit System Default' : `Use Default (${defaultModel?.name || 'GPT-5.2'})`}
                            </SelectItem>
                            {models.map(m => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.name} ({m.provider})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      {selectedOrgId && (
                        <TableCell>
                          <span className="text-xs text-muted-foreground">
                            {systemModel?.name || defaultModel?.name || 'GPT-5.2'}
                          </span>
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge
                          variant={effective.source === 'org' ? 'default' : effective.source === 'system' ? 'secondary' : 'outline'}
                          className={`text-xs ${effective.source === 'org' ? 'bg-blue-500/10 text-blue-600 border-blue-500/20' : ''}`}
                        >
                          {effective.source === 'org' ? 'Org Override' : effective.source === 'system' ? 'System' : 'Default'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {languages.length === 0 && <EmptyRow colSpan={selectedOrgId ? 4 : 3} message="No languages configured" />}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ── MANAGE MODELS ── */}
        <TabsContent value="models">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">Add, edit, or remove AI models available for interpretation</p>
            <Button onClick={() => { setEditModel(null); setForm({ name: '', provider: 'openai', model: '' }); setShowAdd(true); }}
              className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="add-model-button">
              <Plus className="w-4 h-4 mr-2" />Add Model
            </Button>
          </div>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Provider</TableHead><TableHead>Model ID</TableHead>
                <TableHead>Default</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {models.map(m => (
                  <TableRow key={m.id} data-testid={`model-row-${m.id}`}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs capitalize">{m.provider}</Badge></TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{m.model}</TableCell>
                    <TableCell>
                      {m.is_default ? (
                        <Badge className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/20">Default</Badge>
                      ) : (
                        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => handleSetDefault(m.id)} data-testid={`set-default-${m.id}`}>
                          <Star className="w-3 h-3 mr-1" />Set Default
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => openEdit(m)} data-testid={`edit-model-${m.id}`}>Edit</Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(m.id)} data-testid={`delete-model-${m.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {models.length === 0 && <EmptyRow colSpan={5} message="No models. Add one to get started." />}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Model Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="ai-model-dialog">
          <DialogHeader>
            <DialogTitle>{editModel ? 'Edit AI Model' : 'Add AI Model'}</DialogTitle>
            <DialogDescription>Configure an AI model for use in interpretation. All models use the Emergent Universal Key.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Display Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="OpenAI GPT-5.2" data-testid="model-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Provider *</Label>
              <Select value={form.provider} onValueChange={v => setForm(p => ({ ...p, provider: v }))}>
                <SelectTrigger data-testid="model-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Model ID *</Label>
              <Input value={form.model} onChange={e => setForm(p => ({ ...p, model: e.target.value }))} placeholder="gpt-5.2" data-testid="model-id-input" />
              <p className="text-xs text-muted-foreground">The exact model identifier passed to the API (e.g., gpt-5.2, claude-sonnet-4-5-20250514, gemini-3-flash)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAddModel} disabled={!form.name || !form.model} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-model-button">
              {editModel ? 'Update' : 'Add Model'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
